import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Building2, Pencil, Check, X } from "lucide-react";
import { api, formatApiError } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function ServicesDialog({ open, onOpenChange, onSaved }) {
  const [services, setServices] = useState([]);
  const [nom, setNom] = useState("");
  const [saving, setSaving] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const rename = async (s) => {
    const nouveau = renameValue.trim();
    if (!nouveau || nouveau.toUpperCase() === s.nom) {
      setRenamingId(null);
      return;
    }
    try {
      await api.put(`/services/${s.id}`, { nom: nouveau });
      toast.success(`Service renommé : ${s.nom} → ${nouveau.toUpperCase()} (tous les salariés du service sont mis à jour)`);
      setRenamingId(null);
      load();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const load = useCallback(() => {
    api.get("/services").then((r) => setServices(r.data.services)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async () => {
    if (!nom.trim()) return;
    setSaving(true);
    try {
      await api.post("/services", { nom });
      toast.success(`Service "${nom.trim().toUpperCase()}" ajouté`);
      setNom("");
      load();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le service "${s.nom}" ?`)) return;
    try {
      await api.delete(`/services/${s.id}`);
      toast.success(`Service "${s.nom}" supprimé`);
      load();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="services-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-accent" /> Gestion des services</DialogTitle>
          <DialogDescription>Ajoutez ou supprimez les services du magasin. Un service ne peut être supprimé que si aucun salarié n'y est rattaché.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 py-2">
          <Input placeholder="ex : 95 BIERE" value={nom} onChange={(e) => setNom(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} className="bg-background" data-testid="service-name-input" />
          <Button onClick={add} disabled={saving || !nom.trim()} data-testid="add-service-button">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </div>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-border divide-y divide-border/50" data-testid="services-list">
          {services.length === 0 && <p className="px-3 py-6 text-sm text-muted-foreground text-center">Aucun service.</p>}
          {services.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-3 py-2 gap-2" data-testid={`service-item-${s.id}`}>
              {renamingId === s.id ? (
                <div className="flex items-center gap-1.5 flex-1">
                  <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="h-7 bg-background text-sm"
                    autoFocus onKeyDown={(e) => { if (e.key === "Enter") rename(s); if (e.key === "Escape") setRenamingId(null); }} data-testid={`service-rename-input-${s.id}`} />
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-400" onClick={() => rename(s)} data-testid={`service-rename-confirm-${s.id}`}><Check className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRenamingId(null)}><X className="h-3.5 w-3.5" /></Button>
                </div>
              ) : (
                <span className="text-sm font-medium">{s.nom}</span>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground">{s.nb_salaries} salarié(s)</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Renommer" onClick={() => { setRenamingId(s.id); setRenameValue(s.nom); }} data-testid={`rename-service-${s.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-400" onClick={() => remove(s)} data-testid={`delete-service-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
